import React, { useEffect, useRef, useState, useCallback } from 'react';
import '../styles/About.css';
import PlanetMode from './planet-mode/PlanetMode';
import { useAnimationCache } from '../hooks/useAnimationCache';
import {
  animateFadeIn,
  withPerformanceCheck,
  animateWithClass
} from '../utils/animationHelpers';
import {
  tailwindAnimationOptimizer,
  TAILWIND_ANIMATIONS,
  PERFORMANCE_CLASSES,
  animationUtils
} from '../utils/tailwindAnimationOptimizer';
import {
  initializeAnimationCaching,
  batchPromoteElements,
  promoteToRenderLayer
} from '../utils/animationCacheService';
import { WorkerParticleCanvas } from './WorkerParticleCanvas';

interface SkillCategory {
  id: string;
  name: string;
  icon: string;
  description: string;
  technologies: string[];
  position: { x: number; y: number };
  color: string;
  level: number;
  influences: string[];
  dependencies: string[];
}

const About: React.FC = () => {
  const aboutRef = useRef<HTMLElement>(null);
  const skillNodesRef = useRef<HTMLDivElement[]>([]);
  const [activeSkill, setActiveSkill] = useState<string | null>(null);
  const [hoveredSkill, setHoveredSkill] = useState<string | null>(null);
  const [isPlanetMode, setIsPlanetMode] = useState(false);

  // Performance-aware particle system configuration
  const [performanceMode, setPerformanceMode] = useState<'high' | 'medium' | 'low'>('high');
  const [isConstellationVisible, setIsConstellationVisible] = useState(false);

  // Calculate particle count based on performance (reduced from 30 to 15 max)
  const particleCount = performanceMode === 'high' ? 15 : performanceMode === 'medium' ? 8 : 4;
  
  // Constellation particle colors
  const constellationColors = ['#00FF41', '#00D4FF', '#FF0080'];

  // Performance change handler for particle system
  const handleParticlePerformanceChange = useCallback((mode: string, metrics: any) => {
    console.log(`[About] Constellation particle performance mode changed to: ${mode}`, metrics);
  }, []);

  // Initialize animation cache system
  const {
    registerElement,
    animateElement,
    setupScrollAnimations,
    cacheElementState,
    enabled: animationsEnabled
  } = useAnimationCache({
    enabled: true,
    performanceMode: 'high',
    autoPromoteToRenderLayer: true,
    logStats: process.env.NODE_ENV === 'development'
  });

  // Skill categories for constellation mode display
  const skillCategories: SkillCategory[] = [
    {
      id: 'web-design',
      name: 'Web Design',
      icon: '🎨',
      description: 'Modern web interfaces and user experiences with cutting-edge frameworks',
      technologies: ['React', 'Next.js', 'TypeScript', 'Tailwind CSS (v4)', 'Framer Motion', 'Three.js', 'WebGL', 'CSS Grid'],
      position: { x: 15, y: 25 },
      color: '#00FF41',
      level: 95,
      influences: ['game-dev', 'data-systems'],
      dependencies: ['developer-tools']
    },
    {
      id: 'ai-ml',
      name: 'AI & ML',
      icon: '🧠',
      description: 'Neural networks, LLM fine-tuning, and intelligent systems architecture',
      technologies: ['Python', 'PyTorch', 'TensorFlow', 'Hugging Face', 'LangChain', 'OpenAI API', 'Vector DBs', 'CUDA'],
      position: { x: 85, y: 20 },
      color: '#00D4FF',
      level: 88,
      influences: ['computer-vision', 'data-systems'],
      dependencies: ['developer-tools']
    },
    {
      id: 'computer-vision',
      name: 'Computer Vision',
      icon: '👁️',
      description: 'Advanced image processing, facial recognition, and real-time visual AI',
      technologies: ['OpenCV', 'MediaPipe', 'YOLO', 'Face Recognition', 'PIL', 'NumPy', 'Dlib', 'Real-time Processing'],
      position: { x: 25, y: 75 },
      color: '#BF00FF',
      level: 82,
      influences: ['game-dev'],
      dependencies: ['ai-ml', 'developer-tools']
    },
    {
      id: 'developer-tools',
      name: 'DevOps & Tools',
      icon: '🛠️',
      description: 'CLI tools, automation, and developer experience optimization',
      technologies: ['Git', 'Docker', 'GitHub Actions', 'CLI Development', 'Shell Scripting', 'Electron', 'Vite', 'Webpack'],
      position: { x: 75, y: 70 },
      color: '#39FF14',
      level: 90,
      influences: ['web-design', 'ai-ml', 'blockchain', 'data-systems'],
      dependencies: []
    },
    {
      id: 'data-systems',
      name: 'Data Systems',
      icon: '📊',
      description: 'Large-scale data visualization, analysis, and interactive research platforms',
      technologies: ['D3.js', 'Chart.js', 'Pandas', 'PostgreSQL', 'MongoDB', 'Redis', 'Elasticsearch', 'Apache Kafka'],
      position: { x: 50, y: 50 },
      color: '#FFD700',
      level: 85,
      influences: ['web-design'],
      dependencies: ['ai-ml', 'developer-tools']
    },
    {
      id: 'game-dev',
      name: 'Game Dev',
      icon: '🎮',
      description: '3D games, physics simulations, and immersive interactive experiences',
      technologies: ['Three.js', 'WebGL', 'Unity', 'Blender', 'GLSL Shaders', 'Physics Engines', 'Audio APIs', '3D Animation'],
      position: { x: 70, y: 35 },
      color: '#FF4500',
      level: 78,
      influences: [],
      dependencies: ['web-design', 'computer-vision']
    },
    {
      id: 'blockchain',
      name: 'Blockchain',
      icon: '⛓️',
      description: 'Decentralized applications, smart contracts, and Web3 technologies',
      technologies: ['Solidity', 'Ethers.js', 'Web3.js', 'IPFS', 'MetaMask', 'Hardhat', 'OpenZeppelin', 'DeFi Protocols'],
      position: { x: 30, y: 45 },
      color: '#FF0080',
      level: 72,
      influences: [],
      dependencies: ['developer-tools', 'web-design']
    }
  ];

  useEffect(() => {
    // Initialize animation caching system
    if (typeof window !== 'undefined') {
      initializeAnimationCaching();
      
      // Apply mobile optimizations
      animationUtils.optimizeForMobile();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('about--animated');
            
            // Performance-aware animation application
            withPerformanceCheck(() => {
              if (entry.target.classList.contains('about__header')) {
                animateFadeIn(entry.target as HTMLElement, 0);
              } else if (entry.target.classList.contains('skills-browser')) {
                // Batch animate skill nodes
                const skillNodes = entry.target.querySelectorAll('.skill-node') as NodeListOf<HTMLElement>;
                if (skillNodes.length > 0) {
                  batchPromoteElements(Array.from(skillNodes), true);
                  animationUtils.animateOnLoad(Array.from(skillNodes), 150);
                }
              }
            });
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (aboutRef.current) {
      observer.observe(aboutRef.current);
      
      const header = aboutRef.current.querySelector('.about__header');
      const skillsBrowser = aboutRef.current.querySelector('.skills-browser');
      
      if (header) observer.observe(header);
      if (skillsBrowser) observer.observe(skillsBrowser);

      // Setup scroll-triggered animations for highlights
      const highlights = aboutRef.current.querySelectorAll('.about__highlight') as NodeListOf<HTMLElement>;
      if (highlights.length > 0 && animationsEnabled) {
        setupScrollAnimations(
          Array.from(highlights),
          'fadeInTransform',
          { threshold: 0.2, rootMargin: '100px' }
        );
      }
    }

    return () => {
      observer.disconnect();
      tailwindAnimationOptimizer.cleanup();
    };
  }, [animationsEnabled, setupScrollAnimations]);

  // Optimized skill interaction handlers
  const handleSkillClick = (skillId: string) => {
    const isActivating = activeSkill !== skillId;
    setActiveSkill(isActivating ? skillId : null);
    
    if (isActivating && animationsEnabled) {
      withPerformanceCheck(() => {
        const skillElement = skillNodesRef.current.find(
          el => el.getAttribute('data-skill-id') === skillId
        );
        if (skillElement) {
          // Register and animate the active skill
          registerElement(`skill-${skillId}`, skillElement);
          animateElement(`skill-${skillId}`, 'scaleIn', {
            duration: 300,
            easing: 'ease-out'
          });
          
          // Cache the state for future use
          cacheElementState(`skill-${skillId}`, true);
        }
      });
    }
  };

  const handleSkillHover = (skillId: string, isEntering: boolean) => {
    setHoveredSkill(isEntering ? skillId : null);
    
    if (animationsEnabled) {
      withPerformanceCheck(() => {
        const skillElement = skillNodesRef.current.find(
          el => el.getAttribute('data-skill-id') === skillId
        );
        if (skillElement) {
          if (isEntering) {
            registerElement(`skill-hover-${skillId}`, skillElement);
            tailwindAnimationOptimizer.animateElement(
              skillElement,
              'smoothScale',
              { duration: 200, promote: true }
            );
          } else {
            animateWithClass(skillElement, TAILWIND_ANIMATIONS.scaleOut, 200);
          }
        }
      });
    }
  };

  const handleToggleMode = () => {
    setIsPlanetMode(!isPlanetMode);
    setActiveSkill(null);
    setHoveredSkill(null);
    
    // Apply mode transition animation
    if (animationsEnabled) {
      withPerformanceCheck(() => {
        const constellation = aboutRef.current?.querySelector('.skills-constellation') as HTMLElement;
        if (constellation) {
          animateWithClass(constellation, TAILWIND_ANIMATIONS.fadeOut, 300);
          setTimeout(() => {
            animateWithClass(constellation, TAILWIND_ANIMATIONS.fadeIn, 400);
          }, 150);
        }
      });
    }
  };

  // Register skill node refs for optimization
  const registerSkillNode = (element: HTMLDivElement | null, index: number) => {
    if (element && animationsEnabled) {
      skillNodesRef.current[index] = element;
      registerElement(`skill-node-${index}`, element);
      promoteToRenderLayer(element, true);
    }
  };

  // Visibility detection for constellation mode
  useEffect(() => {
    setIsConstellationVisible(!isPlanetMode);
  }, [isPlanetMode]);

  // Detect performance mode based on device capabilities
  useEffect(() => {
    const detectPerformanceMode = () => {
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isLowEnd = navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4;
      
      if (isMobile || isLowEnd) return 'low';
      if (window.innerWidth < 1024) return 'medium';
      return 'high';
    };

    setPerformanceMode(detectPerformanceMode());

    const handleResize = () => setPerformanceMode(detectPerformanceMode());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <section id="about" className="about" ref={aboutRef}>
      {/* Journey Section */}
      <div className="about__journey">
        <div className="container">
          <div className="about__header">
            <h2 className="about__title">My Journey</h2>
            <p className="about__subtitle">
              AI Architect & Full-Stack Developer. Ex-Jack-of-All-Trades with a deep interest in generative AI and building the future.
              <em>"If your life is just a collection of stories, might as well make them interesting ones."</em>
            </p>
          </div>

          <div className="about__journey-content">
            <div className="about__text">
              <h3>The Path</h3>
              <p>
                I'm an AI architect and engineer who believes in the <strong>Full-Ass™</strong> philosophy - 
                "I don't half ass anything." My path has been deliberately diverse: I used to release music, 
                ran my own indie label, studied history, and moonlighted in aerospace engineering in high school.
              </p>
              <p>
                Currently focused on <strong>while alive: learn("Everything from all angles")</strong> - 
                building AI-powered tools, optimizing developer workflows, and creating systems that enhance human capability. 
                Every project is an opportunity to push boundaries and solve problems from unique angles.
              </p>
              
              <div className="about__highlights">
                <div className="about__highlight">
                  <h4>AI & Machine Learning</h4>
                  <p>LLM fine-tuning, neural networks, computer vision, multi-agent systems</p>
                </div>
                <div className="about__highlight">
                  <h4>Developer Tools</h4>
                  <p>QGit (2,847 downloads), SafeSpace, FaxMachine, workflow optimization</p>
                </div>
                <div className="about__highlight">
                  <h4>Creative Engineering</h4>
                  <p>3D games, music interfaces, data visualization, block editors</p>
                </div>
              </div>

              <div className="about__philosophy">
                <h4>Current Pursuits</h4>
                <div className="about__code-block">
                  <code>
                    while alive:<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;learn("Everything from all angles")<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;if reached_infinity:<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;break
                  </code>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Skills Section with Mode Toggle */}
      <div className="about__skills-section">
        <div className="container">
          <div className="about__skills-header">
            <h2 className="about__skills-title">Skills Universe</h2>
            <p className="about__skills-subtitle">
              {isPlanetMode 
                ? "Explore the skills solar system. Each planet represents a skill domain with orbiting technology moons."
                : "Navigate through my technical constellation. Each connection represents data flow, dependencies, and knowledge transfer between domains."
              }
            </p>
          </div>

          {/* Mode Toggle Controls */}
          <div className="skills-mode-controls">
            <button
              className={`skills-mode-toggle ${isPlanetMode ? 'skills-mode-toggle--planet' : 'skills-mode-toggle--constellation'} ${PERFORMANCE_CLASSES.gpuAcceleration} ${PERFORMANCE_CLASSES.willChangeTransform}`}
              onClick={handleToggleMode}
              aria-label={`Switch to ${isPlanetMode ? 'Constellation' : 'Planet'} Mode`}
            >
              <span className="mode-toggle-icon">
                {isPlanetMode ? '🌌' : '🪐'}
              </span>
              <span className="mode-toggle-text">
                {isPlanetMode ? 'Constellation Mode' : 'Planet Mode'}
              </span>
            </button>
          </div>

          <div className="skills-browser">
            {/* Planet Mode */}
            {isPlanetMode ? (
              <PlanetMode 
                isActive={isPlanetMode} 
                onToggleMode={handleToggleMode}
              />
            ) : (
              /* Optimized Constellation Mode */
              <div className={`skills-constellation ${activeSkill ? 'constellation--focused' : ''} ${hoveredSkill ? 'constellation--hovered' : ''} ${PERFORMANCE_CLASSES.gpuAcceleration}`}>
                
                {/* Enhanced Background Effects */}
                <div className="constellation-bg">
                  {/* Animated Grid */}
                  <div className={`constellation-grid ${PERFORMANCE_CLASSES.willChangeTransform}`}></div>
                  
                  {/* Pulsing Core */}
                  <div className={`constellation-core ${PERFORMANCE_CLASSES.willChangeTransform}`}></div>
                  
                  {/* Worker-Based Constellation Particles - Replaces manual DOM particles */}
                  {!isPlanetMode && (
                    <WorkerParticleCanvas
                      className="constellation-particles-worker"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        zIndex: 1,
                        opacity: activeSkill ? 0.3 : 0.7,
                        transition: 'opacity 0.3s ease',
                        contain: 'strict'
                      }}
                      particleCount={particleCount}
                      colors={constellationColors}
                      performanceMode={performanceMode}
                      enableGlow={performanceMode !== 'low'}
                      enableTrails={false}
                      isVisible={isConstellationVisible}
                      onPerformanceChange={handleParticlePerformanceChange}
                    />
                  )}
                </div>

                {/* Optimized Skill Nodes */}
                {skillCategories.map((skill, index) => (
                  <div
                    key={skill.id}
                    ref={(el) => registerSkillNode(el, index)}
                    data-skill-id={skill.id}
                    className={`skill-node ${activeSkill === skill.id ? 'skill-node--active' : ''} ${hoveredSkill === skill.id ? 'skill-node--hovered' : ''} ${PERFORMANCE_CLASSES.gpuAcceleration} ${PERFORMANCE_CLASSES.willChangeTransform}`}
                    style={{
                      '--x': `${skill.position.x}%`,
                      '--y': `${skill.position.y}%`,
                      '--color': skill.color,
                      '--delay': `${index * 0.2}s`,
                      '--level': skill.level / 100
                    } as React.CSSProperties}
                    onClick={() => handleSkillClick(skill.id)}
                    onMouseEnter={() => handleSkillHover(skill.id, true)}
                    onMouseLeave={() => handleSkillHover(skill.id, false)}
                  >
                    {/* Skill Level Ring */}
                    <div className="skill-node__level-ring">
                      <svg viewBox="0 0 100 100" className="level-ring-svg">
                        <circle
                          cx="50"
                          cy="50"
                          r="45"
                          className="level-ring-bg"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="45"
                          className="level-ring-progress"
                          style={{
                            '--progress': skill.level / 100
                          } as React.CSSProperties}
                        />
                      </svg>
                    </div>

                    {/* Enhanced Core */}
                    <div className={`skill-node__core ${PERFORMANCE_CLASSES.willChangeTransform}`}>
                      <div className="skill-node__glow"></div>
                      <span className="skill-node__icon">{skill.icon}</span>
                      <div className="skill-node__pulse"></div>
                    </div>

                    {/* Floating Label */}
                    <div className={`skill-node__label ${PERFORMANCE_CLASSES.willChangeTransform}`}>
                      <span className="skill-node__name">{skill.name}</span>
                      <span className="skill-node__level">{skill.level}%</span>
                    </div>

                    {/* Technology Orbit */}
                    {activeSkill === skill.id && (
                      <div className="tech-orbit">
                        {skill.technologies.map((tech, techIndex) => {
                          const angle = (techIndex / skill.technologies.length) * 2 * Math.PI;
                          const radius = 120;
                          const x = Math.cos(angle) * radius;
                          const y = Math.sin(angle) * radius;
                          
                          return (
                            <div
                              key={tech}
                              className={`tech-satellite ${PERFORMANCE_CLASSES.gpuAcceleration} ${PERFORMANCE_CLASSES.willChangeTransform}`}
                              style={{
                                '--tech-x': `${x}px`,
                                '--tech-y': `${y}px`,
                                '--tech-delay': `${techIndex * 0.1}s`,
                                '--orbit-delay': `${techIndex * 0.5}s`,
                                '--color': skill.color
                              } as React.CSSProperties}
                            >
                              <span className="tech-name">{tech}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}

                {/* Enhanced Details Panel */}
                {activeSkill && (
                  <div className={`skill-details-panel ${PERFORMANCE_CLASSES.gpuAcceleration}`}>
                    {/* Close Button */}
                    <button
                      className="skill-details-close-button"
                      onClick={() => setActiveSkill(null)}
                      aria-label="Close skill details panel"
                    >
                      ✕
                    </button>
                    
                    {(() => {
                      const skill = skillCategories.find(s => s.id === activeSkill);
                      return skill ? (
                        <>
                          <div className="skill-details__header">
                            <div className="skill-details__icon" style={{ color: skill.color }}>
                              {skill.icon}
                            </div>
                            <div className="skill-details__title-wrapper">
                              <h3 className="skill-details__title">{skill.name}</h3>
                              <div className="skill-details__proficiency">
                                <span className="proficiency-label">Proficiency</span>
                                <div className="proficiency-bar">
                                  <div 
                                    className="proficiency-fill" 
                                    style={{ 
                                      width: `${skill.level}%`,
                                      backgroundColor: skill.color 
                                    }}
                                  ></div>
                                </div>
                                <span className="proficiency-value">{skill.level}%</span>
                              </div>
                            </div>
                          </div>
                          <p className="skill-details__description">{skill.description}</p>
                          <div className="skill-details__tech-grid">
                            {skill.technologies.map((tech, index) => (
                              <div 
                                key={tech} 
                                className={`tech-chip ${PERFORMANCE_CLASSES.gpuAcceleration}`}
                                style={{
                                  '--chip-delay': `${index * 0.05}s`,
                                  '--chip-color': skill.color
                                } as React.CSSProperties}
                              >
                                <span className="tech-chip__name">{tech}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Enhanced Instructions */}
            <div className="skills-instructions">
              <div className="instruction-text">
                <span className="instruction-primary">
                  {isPlanetMode ? 'Navigate the Skills Solar System' : 'Navigate the Knowledge Universe'}
                </span>
                <span className="instruction-secondary">
                  {isPlanetMode 
                    ? 'Click planets to zoom in and explore their technology moons • Use mode toggle to switch views'
                    : 'Click nodes to explore technologies • Hover for skill details • Background shows neural pathways'
                  }
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About; 